import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import React, { useEffect, useMemo, useState } from "react";
import Topbar from "../../../../components/topbar/TopBar";
import { createCancellationReasonUseCase } from "../domain/usecases/createCancellationReasonUseCase";
import { deleteCancellationReasonUseCase } from "../domain/usecases/deleteCancellationReasonUseCase";
import { getCancellationReasonListUseCase } from "../domain/usecases/getCancellationReasonListUseCase";
import { updateCancellationReasonUseCase } from "../domain/usecases/updateCancellationReasonUseCase";

const USER_OPTIONS = [
  { value: "CLIENT", label: "CLIENT" },
  { value: "DRIVER", label: "DRIVER" },
  { value: "ENTERPRISE", label: "ENTEPRISE" },
  { value: "SYSTEM", label: "SYSTEM" },
];

function createInitialForm() {
  return {
    code: "",
    reason: "",
    user: "CLIENT",
  };
}

function createPayload(form) {
  return {
    code: form.code.trim().toUpperCase(),
    name: form.reason.trim(),
    actor_type: form.user,
    requires_comment: false,
    active: true,
  };
}

function createFormFromRecord(record) {
  if (!record) {
    return createInitialForm();
  }

  return {
    code: record.code || "",
    reason: record.name || "",
    user: record.actor_type || "CLIENT",
  };
}

function isFormReady(form) {
  return (
    form.code.trim().length > 0 &&
    form.code.trim().length <= 50 &&
    form.reason.trim().length > 0 &&
    form.reason.trim().length <= 100 &&
    USER_OPTIONS.some((option) => option.value === form.user)
  );
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return response?.success === true || message.includes("success");
}

export const CancellationReasonPage = () => {
  const [form, setForm] = useState(createInitialForm());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(createInitialForm());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const canSubmit = useMemo(() => isFormReady(form), [form]);
  const canUpdate = useMemo(() => isFormReady(editForm), [editForm]);

  const loadCancellationReasons = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      const reasons = await getCancellationReasonListUseCase();
      setRecords(reasons);
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo cargar la lista de motivos.",
      });
      setRecords([]);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadCancellationReasons();
  }, []);

  const handleCodeChange = (event) => {
    setForm((prev) => ({
      ...prev,
      code: event.target.value.toUpperCase(),
    }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditCodeChange = (event) => {
    setEditForm((prev) => ({
      ...prev,
      code: event.target.value.toUpperCase(),
    }));
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;

    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreate = async () => {
    const payload = createPayload(form);

    try {
      setSaving(true);
      const response = await createCancellationReasonUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo crear el motivo de cancelacion.");
      }

      setForm(createInitialForm());
      setMessage({
        open: true,
        type: "success",
        text: "Motivo de cancelacion creado correctamente.",
      });
      await loadCancellationReasons({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo crear el motivo de cancelacion.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setEditForm(createFormFromRecord(record));
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setSelectedRecord(null);
    setEditForm(createInitialForm());
  };

  const handleUpdate = async () => {
    const payload = {
      id: selectedRecord.id,
      ...createPayload(editForm),
    };

    try {
      setSaving(true);
      const response = await updateCancellationReasonUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo actualizar el motivo de cancelacion.");
      }

      handleCloseEdit();
      setMessage({
        open: true,
        type: "success",
        text: "Motivo de cancelacion actualizado correctamente.",
      });
      await loadCancellationReasons({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo actualizar el motivo de cancelacion.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = (record) => {
    setSelectedRecord(record);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setSelectedRecord(null);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const response = await deleteCancellationReasonUseCase(selectedRecord.id);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo eliminar el motivo de cancelacion.");
      }

      handleCloseDelete();
      setMessage({
        open: true,
        type: "success",
        text: "Motivo de cancelacion eliminado correctamente.",
      });
      await loadCancellationReasons({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo eliminar el motivo de cancelacion.",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 960,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1, md: 2 },
                  py: { xs: 2, md: 2 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{ color: "#fff !important" }}
                >
                  CREAR MOTIVO DE CANCELACION
                </Typography>
              </Box>

              <Box sx={{ p: { xs: 1, md: 3 } }}>
                <Stack spacing={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      mx: "auto",
                      width: "100%",
                      maxWidth: 520,
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      p: { xs: 1, md: 2 },
                    }}
                  >
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Codigo"
                        name="code"
                        value={form.code}
                        onChange={handleCodeChange}
                        inputProps={{ maxLength: 50 }}
                        helperText={`${form.code.length}/50`}
                      />
                      <TextField
                        fullWidth
                        label="Motivo"
                        name="reason"
                        value={form.reason}
                        onChange={handleChange}
                        inputProps={{ maxLength: 100 }}
                        helperText={`${form.reason.length}/100`}
                      />
                      <TextField
                        fullWidth
                        select
                        label="Usuario"
                        name="user"
                        value={form.user}
                        onChange={handleChange}
                      >
                        {USER_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleCreate}
                        disabled={!canSubmit || saving}
                        sx={{
                          mt: 1,
                          minHeight: 50,
                          borderRadius: 2,
                          textTransform: "uppercase",
                          fontWeight: 700,
                          bgcolor: "#165c53",
                          color: "#fff",
                          "&:hover": {
                            bgcolor: "#12483f",
                          },
                        }}
                      >
                        {saving ? "CREANDO..." : "CREAR"}
                      </Button>
                    </Stack>
                  </Paper>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      overflowX: "auto",
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow
                          sx={{
                            bgcolor: "#f4faf7",
                            "& th": {
                              fontWeight: 800,
                              color: "#12483f",
                            },
                          }}
                        >
                          <TableCell>Codigo</TableCell>
                          <TableCell>Motivo</TableCell>
                          <TableCell>Usuario</TableCell>
                          <TableCell align="right">Opciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Stack
                                alignItems="center"
                                spacing={2}
                                sx={{ py: 4 }}
                              >
                                <CircularProgress />
                                <Typography color="text.secondary">
                                  Cargando motivos...
                                </Typography>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ) : records.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography sx={{ py: 3 }} color="text.secondary">
                                No hay motivos registrados.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          records.map((record) => (
                            <TableRow key={record.id} hover>
                              <TableCell>{record.code}</TableCell>
                              <TableCell>{record.name}</TableCell>
                              <TableCell>{record.actor_type}</TableCell>
                              <TableCell align="right">
                                <Stack
                                  direction="row"
                                  justifyContent="flex-end"
                                  spacing={1}
                                >
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleOpenEdit(record)}
                                    color="success"
                                  >
                                    Editar
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() => handleOpenDelete(record)}
                                  >
                                    Eliminar
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </Box>
            </Paper>
          </Stack>

          <Dialog
            fullWidth
            maxWidth="sm"
            open={editOpen}
            onClose={handleCloseEdit}
          >
            <DialogTitle>Editar motivo de cancelacion</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                  fullWidth
                  label="Codigo"
                  name="code"
                  value={editForm.code}
                  onChange={handleEditCodeChange}
                  inputProps={{ maxLength: 50 }}
                  helperText={`${editForm.code.length}/50`}
                />
                <TextField
                  fullWidth
                  label="Motivo"
                  name="reason"
                  value={editForm.reason}
                  onChange={handleEditChange}
                  inputProps={{ maxLength: 100 }}
                  helperText={`${editForm.reason.length}/100`}
                />
                <TextField
                  fullWidth
                  select
                  label="Usuario"
                  name="user"
                  value={editForm.user}
                  onChange={handleEditChange}
                >
                  {USER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEdit} disabled={saving}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={!canUpdate || saving}
              >
                {saving ? "Actualizando..." : "Actualizar"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={deleteOpen} onClose={handleCloseDelete}>
            <DialogTitle>Eliminar esta razon?</DialogTitle>
            <DialogActions>
              <Button onClick={handleCloseDelete} disabled={deleting}>
                No
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Eliminando..." : "Si,Eliminar"}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
};
